/**
 * Advanced Ranking Rules Types
 *
 * These types support configurable ranking rules for competitions,
 * including custom aggregation formulas and advanced tie-breaking rules.
 */
// ============================================
// Aggregation Types
// ============================================
/**
 * Type of aggregation for combining stage scores
 */
export var AggregationType;
(function (AggregationType) {
    AggregationType["SUM"] = "SUM";
    AggregationType["WEIGHTED_SUM"] = "WEIGHTED_SUM";
    AggregationType["CUSTOM"] = "CUSTOM";
})(AggregationType || (AggregationType = {}));
/**
 * Strategy for handling multiple attempts in a stage
 */
export var AttemptStrategy;
(function (AttemptStrategy) {
    AttemptStrategy["MAX"] = "MAX";
    AttemptStrategy["MIN"] = "MIN";
    AttemptStrategy["AVG"] = "AVG";
    AttemptStrategy["LATEST"] = "LATEST";
    AttemptStrategy["FIRST"] = "FIRST";
})(AttemptStrategy || (AttemptStrategy = {}));
// ============================================
// Tiebreaker Rule Types
// ============================================
/**
 * All tie-breaker rule types
 */
export var TiebreakerRuleType;
(function (TiebreakerRuleType) {
    // Time-based
    TiebreakerRuleType["TIME_ALWAYS"] = "TIME_ALWAYS";
    TiebreakerRuleType["TIME_IF_PERFECT"] = "TIME_IF_PERFECT";
    TiebreakerRuleType["TIME_IF_ABOVE_THRESHOLD"] = "TIME_IF_ABOVE_THRESHOLD";
    // Stage-based
    TiebreakerRuleType["STAGE_SCORE"] = "STAGE_SCORE";
    TiebreakerRuleType["STAGE_TIME"] = "STAGE_TIME";
    // Performance-based
    TiebreakerRuleType["BEST_SINGLE_ATTEMPT"] = "BEST_SINGLE_ATTEMPT";
    TiebreakerRuleType["CONSISTENCY"] = "CONSISTENCY";
    TiebreakerRuleType["IMPROVEMENT"] = "IMPROVEMENT";
    // Head-to-head (v2 feature)
    TiebreakerRuleType["HEAD_TO_HEAD"] = "HEAD_TO_HEAD";
    // Fallbacks
    TiebreakerRuleType["REGISTRATION_ORDER"] = "REGISTRATION_ORDER";
    TiebreakerRuleType["RANDOM"] = "RANDOM";
    TiebreakerRuleType["MANUAL"] = "MANUAL";
})(TiebreakerRuleType || (TiebreakerRuleType = {}));
// ============================================
// Legacy Types (for backward compatibility)
// ============================================
/**
 * Legacy tiebreaker type (simplified)
 * @deprecated Use TiebreakerRuleType instead
 */
export var TiebreakerType;
(function (TiebreakerType) {
    TiebreakerType["TIME_IF_PERFECT"] = "TIME_IF_PERFECT";
    TiebreakerType["TIME_ALWAYS"] = "TIME_ALWAYS";
    TiebreakerType["SCORE_THEN_TIME"] = "SCORE_THEN_TIME";
    TiebreakerType["NONE"] = "NONE";
})(TiebreakerType || (TiebreakerType = {}));
// ============================================
// Ranking History Types
// ============================================
/**
 * What triggered a ranking snapshot recalculation.
 * (Lowercase wire values — shared by api ranking-history DTO and web client.)
 */
export var TriggerType;
(function (TriggerType) {
    TriggerType["MANUAL"] = "manual";
    TriggerType["AUTO"] = "auto";
    TriggerType["SCORE_ADDED"] = "score_added";
})(TriggerType || (TriggerType = {}));
/**
 * Convert scoring forms FormulaNode to ranking rules FormulaNode
 * This allows the token-based builder to work with ranking rules
 */
export function convertScoringFormulaToRankingFormula(scoringNode, stageMap) {
    if (!scoringNode)
        return null;
    switch (scoringNode.type) {
        case 'number':
            return { type: 'number', value: scoringNode.value };
        case 'field': {
            // Convert field reference to stage variable
            const fieldId = String(scoringNode.value);
            const stageInfo = stageMap.get(fieldId);
            if (!stageInfo)
                return null;
            return {
                type: 'stage',
                stageId: stageInfo.id,
                stageName: stageInfo.name,
                property: scoringNode.property || 'score',
            };
        }
        case 'operator': {
            const children = (scoringNode.children || []).map((c) => convertScoringFormulaToRankingFormula(c, stageMap));
            if (children.some((c) => c === null))
                return null;
            if (children.length < 2)
                return null;
            return {
                type: 'binary',
                operator: scoringNode.value,
                left: children[0],
                right: children[1],
            };
        }
        case 'function': {
            const children = (scoringNode.children || []).map((c) => convertScoringFormulaToRankingFormula(c, stageMap));
            if (children.some((c) => c === null))
                return null;
            const lowerName = String(scoringNode.value).toLowerCase();
            // IF(condition, then, else?) → conditional node.
            // The token-based builder surfaces this as a function call; we promote it
            // to the ranking engine's native conditional so the backend IF semantics
            // (non-zero condition is truthy) apply during aggregation.
            if (lowerName === 'if') {
                if (children.length < 2)
                    return null;
                const node = {
                    type: 'conditional',
                    condition: children[0],
                    thenBranch: children[1],
                };
                if (children[2])
                    node.elseBranch = children[2];
                return node;
            }
            // Map lowercase function names to uppercase ranking function names
            const functionNameMap = {
                min: 'MIN',
                max: 'MAX',
                avg: 'AVG',
                sum: 'SUM',
                count: 'COUNT',
                median: 'MEDIAN',
            };
            const funcName = functionNameMap[lowerName];
            if (!funcName)
                return null;
            return {
                type: 'function',
                function: funcName,
                arguments: children,
            };
        }
        default:
            return null;
    }
}
// ============================================
// Type Guards
// ============================================
/**
 * Check if a config is in the advanced ranking config format
 * Used to determine which tiebreaker resolution method to use
 *
 * Note: This accepts unknown input to be flexible with different
 * config sources (database JSON, API requests, etc.)
 */
export function isAdvancedRankingConfig(config) {
    if (!config || typeof config !== 'object')
        return false;
    const cfg = config;
    // Check for version field (present in advanced config)
    if ('version' in cfg && typeof cfg.version === 'number')
        return true;
    // Check for advanced tiebreaker structure
    if ('tiebreaker' in cfg &&
        cfg.tiebreaker !== null &&
        typeof cfg.tiebreaker === 'object') {
        const tiebreaker = cfg.tiebreaker;
        if ('rules' in tiebreaker && Array.isArray(tiebreaker.rules)) {
            return true;
        }
    }
    return false;
}
/**
 * Convert ranking rules FormulaNode to scoring forms FormulaNode
 * This allows loading existing ranking formulas into the token-based builder
 */
export function convertRankingFormulaToScoringFormula(rankingNode, fieldIdMap) {
    if (!rankingNode)
        return null;
    switch (rankingNode.type) {
        case 'number':
            return { type: 'number', value: rankingNode.value };
        case 'stage': {
            // Convert stage variable to field reference
            const fieldId = fieldIdMap.get(rankingNode.stageId);
            if (!fieldId)
                return null;
            // Always emit property (defaulting to 'score') so the token builder can
            // round-trip the exact selector item (score / time / attempts) back.
            return {
                type: 'field',
                value: fieldId,
                property: rankingNode.property || 'score',
            };
        }
        case 'binary': {
            const left = convertRankingFormulaToScoringFormula(rankingNode.left, fieldIdMap);
            const right = convertRankingFormulaToScoringFormula(rankingNode.right, fieldIdMap);
            if (!left || !right)
                return null;
            return {
                type: 'operator',
                value: rankingNode.operator,
                children: [left, right],
            };
        }
        case 'function': {
            const args = (rankingNode.arguments || []).map((arg) => convertRankingFormulaToScoringFormula(arg, fieldIdMap));
            if (args.some((a) => a === null))
                return null;
            // Map uppercase function names to lowercase (for scoring forms)
            const functionNameMap = {
                MIN: 'min',
                MAX: 'max',
                AVG: 'avg',
                SUM: 'sum',
                COUNT: 'count',
                MEDIAN: 'median',
            };
            const funcName = functionNameMap[rankingNode.function];
            if (!funcName)
                return null;
            return {
                type: 'function',
                value: funcName,
                children: args,
            };
        }
        case 'conditional': {
            // Round-trip a conditional back to an IF() function call so the
            // token-based builder can edit formulas it previously produced.
            const condition = convertRankingFormulaToScoringFormula(rankingNode.condition, fieldIdMap);
            const thenBranch = convertRankingFormulaToScoringFormula(rankingNode.thenBranch, fieldIdMap);
            if (!condition || !thenBranch)
                return null;
            const children = [condition, thenBranch];
            if (rankingNode.elseBranch) {
                const elseBranch = convertRankingFormulaToScoringFormula(rankingNode.elseBranch, fieldIdMap);
                if (!elseBranch)
                    return null;
                children.push(elseBranch);
            }
            return {
                type: 'function',
                value: 'if',
                children,
            };
        }
        default:
            return null;
    }
}
//# sourceMappingURL=ranking.js.map